import { debounce } from 'lodash-es';
import type { DebouncedFunc } from 'lodash-es';
import type { DirectiveBinding } from 'vue';

type ObserveHandler = (
  isIntersecting: boolean,
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver,
) => void;

type DebouncedObserveHandler = DebouncedFunc<ObserveHandler>;

declare global {
  interface HTMLElement {
    _observe?: Record<
      string,
      { init: boolean; observer: IntersectionObserver; debouncedHandler?: DebouncedObserveHandler }
    >;
  }
}

export interface ObserveDirectiveBinding extends Omit<DirectiveBinding, 'modifiers' | 'value'> {
  value?: ObserveHandler | { handler: ObserveHandler; options?: IntersectionObserverInit };
  modifiers: {
    once?: boolean;
  };
}

const ThrottleLimit = 100;

function mounted(el: HTMLElement, binding: ObserveDirectiveBinding) {
  if (!IntersectionObserver) {
    return;
  }
  const modifiers = binding.modifiers || {};
  const value = binding.value;
  const { handler, options } = typeof value === 'object' ? value : { handler: value, options: {} };
  const debouncedHandler = handler ? debounce(handler, ThrottleLimit) : undefined;

  const observer = new IntersectionObserver((entries: IntersectionObserverEntry[] = []) => {
    const _observe = el._observe?.[binding.instance!.$.uid];
    if (!_observe) {
      return;
    }

    const isIntersecting = entries.some(entry => entry.isIntersecting);

    if (modifiers.once) {
      if (isIntersecting && handler) {
        handler(isIntersecting, entries, observer);
        unmounted(el, binding);
      }
      return;
    }

    if (debouncedHandler && (isIntersecting || _observe.init)) {
      debouncedHandler(isIntersecting, entries, observer);
    }

    _observe.init = true;
  }, options);

  el._observe = Object(el._observe);
  el._observe![binding.instance!.$.uid] = { init: false, observer, debouncedHandler };

  observer.observe(el);
}

function unmounted(el: HTMLElement, binding: ObserveDirectiveBinding) {
  if (!IntersectionObserver) {
    return;
  }
  const observe = el._observe?.[binding.instance!.$.uid];
  if (!observe) {
    return;
  }

  // 清理 IntersectionObserver
  observe.observer.unobserve(el);
  observe.observer.disconnect();

  if (observe.debouncedHandler && typeof observe.debouncedHandler.cancel === 'function') {
    observe.debouncedHandler.cancel();
  }

  delete el._observe![binding.instance!.$.uid];

  // 如果该元素没有其他观察者，清理整个 _observe 对象
  if (Object.keys(el._observe || {}).length === 0) {
    delete el._observe;
  }
}

export const Intersect = {
  mounted,
  unmounted,
};

export default Intersect;
